import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { XCircle, Mail, Phone, FileText, AlertTriangle, ArrowRightCircle, Clock, User, RefreshCcw } from 'lucide-react';
import Header from "../../components/Public/ui/Header";
import Footer from "../../components/Public/ui/Footer";
import { getApplicationStatusHistory } from "../../services/passportApi";
import DemoToolWidget from "../../components/DemoToolWidget";
import NeedAssistanceBanner from "../../components/common/NeedAssistanceBanner";

export default function RejectedPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const applicationId = location.state?.applicationId;

  const [rejection, setRejection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const handleApplyAgain = () => {
    navigate("/apply", { state: { isRenewal: false, renewalData: null } });
  };

  useEffect(() => {
    const fetchRejectionReasons = async () => {
      if (!applicationId) { setLoading(false); return; }
      try {
        setLoading(true);
        const data = await getApplicationStatusHistory(applicationId);
        if (data.success) {
          setRejection(data.rejections && data.rejections.length > 0 ? data.rejections[0] : null);
        } else {
          setError(data.message);
        }
      } catch (err) {
        setError('Failed to load rejection reasons');
      } finally {
        setLoading(false);
      }
    };
    fetchRejectionReasons();
  }, [applicationId]);

  const reasonLines = rejection?.change_reason
    ? rejection.change_reason.split('\n').filter(line => line.trim().length > 0)
    : [];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col font-sans">
      <Header />

      <main className="flex-grow flex items-center justify-center p-4 sm:p-6"
        style={{
          backgroundImage: "radial-gradient(circle at center, rgba(255,255,255,0.9) 0%, rgba(241,245,249,1) 100%), url('/assets/Images/SLUDI-background.png')",
          backgroundSize: "cover",
          backgroundAttachment: "fixed"
        }}>

        <div className="w-full max-w-4xl bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">

          {/* Compact Rejection Header */}
          <div className="bg-rose-50/50 border-b border-rose-100 p-6 sm:px-8">
            <div className="flex items-center gap-5">
              <div className="bg-rose-600 p-3 rounded-xl shadow-md shadow-rose-200">
                <XCircle className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-900 leading-tight">Application rejected</h2>
                <p className="text-slate-500 text-sm mt-0.5">Your passport application has been reviewed and was not approved.</p>
              </div>
            </div>
          </div>

          <div className="p-6 sm:p-8 space-y-6">

            {/* 1. Rejection Reason Card */}
            <section className="bg-rose-50/30 rounded-xl p-5 border border-rose-100">
              <h3 className="text-[10px] font-bold text-rose-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                <AlertTriangle size={14} /> Reason for rejection
              </h3>

              {loading ? (
                <p className="text-slate-500 text-sm animate-pulse italic">Retrieving feedback...</p>
              ) : error || !rejection ? (
                <p className="text-slate-600 text-sm italic">No specific reasons were provided. Please resubmit or contact support.</p>
              ) : (
                <div className="space-y-4">
                  <div className="flex gap-4 text-[10px] font-bold text-slate-400 uppercase border-b border-rose-100 pb-3">
                    <span className="flex items-center gap-1"><Clock size={12} /> {new Date(rejection.changed_at).toLocaleDateString()}</span>
                    <span className="flex items-center gap-1"><User size={12} /> Reviewer ID: {rejection.rejected_by || 'ADMIN'}</span>
                  </div>

                  <ul className="space-y-3">
                    {reasonLines.map((line, index) => (
                      <li key={index} className="flex gap-3">
                        <span className="flex-shrink-0 w-5 h-5 bg-rose-600 text-white text-[10px] font-bold flex items-center justify-center rounded-full mt-0.5">
                          {index + 1}
                        </span>
                        <p className="text-sm text-slate-800 font-medium leading-relaxed">{line.trim()}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </section>

            {/* 2. Resubmit Action Card */}
            <section className="bg-blue-600 rounded-xl p-6 text-white flex flex-col md:flex-row items-center justify-between gap-6 shadow-lg shadow-blue-100">
              <div className="max-w-md text-center md:text-left">
                <h3 className="text-lg font-bold mb-1">Ready to reapply?</h3>
                <p className="text-blue-100 text-sm">Review the feedback, correct the errors, and resubmit. You may not need to pay full fees if resubmitted within 60 days.</p>
              </div>
              <button
                onClick={handleApplyAgain}
                className="flex-shrink-0 flex items-center gap-2 px-6 py-3 bg-white text-blue-600 rounded-xl font-bold text-sm hover:bg-blue-50 transition-all shadow-sm"
              >
                <RefreshCcw size={16} />
                Resubmit application
              </button>
            </section>

            {/* 4. Support Footer */}
            <NeedAssistanceBanner 
              type="error" 
              message="Contact us if you believe this rejection was made in error."
              email="passport.support@gov.com"
              phone="+94 123 45678"
            />
          </div>
        </div>
      </main>
      <Footer />
      <DemoToolWidget context="rejected" applicationData={{ application_number: applicationId, status: 'rejected' }} />
    </div>
  );
}